// api/files.ts

import { AxiosResponse } from 'axios';
import $api from './instance';

/*
uploadFile - завантаження файла з base64
uploadAvatar - спеціальний метод для аватарів
getMyFiles - файли користувача з пагінацією та фільтрами
getFileById - отримання конкретного файла
deleteFile - видалення файла
getFilesByEntity - файли прив'язані до сутності
* */

export interface UploadFileDto {
  content: string; // Base64 encoded file content
  originalName: string;
  category:
    | 'avatar'
    | 'document'
    | 'attachment'
    | 'property_image'
    | 'unit_image';
  entityType?: string;
  entityId?: number;
  isPublic?: boolean;
  metadata?: Record<string, any>;
}

export interface UploadAvatarDto {
  content: string; // Base64 encoded file content
  originalName: string;
}

export interface UpdateFileNameDto {
  fileName: string;
}

export interface FileResponseDto {
  id: number;
  originalName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  category: string;
  uploadedBy: number;
  entityType?: string;
  entityId?: number;
  isPublic: boolean;
  metadata?: Record<string, any>;
  createdAt: Date;
  url: string;
}

export interface GetUserFilesDto {
  category?:
    | 'avatar'
    | 'document'
    | 'attachment'
    | 'property_image'
    | 'unit_image';
  entityType?: string;
  entityId?: number;
  page?: number;
  limit?: number;
}

export interface UserFilesResponse {
  files: FileResponseDto[];
  total: number;
  page: number;
  totalPages: number;
}

class FilesApi {
  // Завантажити файл
  async uploadFile(
    data: UploadFileDto,
  ): Promise<AxiosResponse<FileResponseDto>> {
    return $api.post('/files/upload', data);
  }

  // Завантажити файл для конкретного користувача
  async uploadFileForUser(
    userId: number,
    data: UploadFileDto,
  ): Promise<AxiosResponse<FileResponseDto>> {
    return $api.post(`/files/user/${userId}/upload`, data);
  }

  // Завантажити аватар (спеціальний метод)
  async uploadAvatar(
    data: UploadAvatarDto,
  ): Promise<AxiosResponse<FileResponseDto>> {
    return $api.post('/files/upload/avatar', data);
  }

  // Отримати файли поточного користувача
  async getMyFiles(
    params?: GetUserFilesDto,
  ): Promise<AxiosResponse<UserFilesResponse>> {
    return $api.get('/files/my', { params });
  }

  // Отримати файли конкретного користувача за ID
  async getFilesByUserId(
    userId: number,
    params?: GetUserFilesDto,
  ): Promise<AxiosResponse<UserFilesResponse>> {
    return $api.get(`/files/user/${userId}`, { params });
  }

  // Отримати файл за ID
  async getFileById(id: number): Promise<AxiosResponse<FileResponseDto>> {
    return $api.get(`/files/${id}`);
  }

  // Видалити файл
  async deleteFile(id: number): Promise<AxiosResponse<{ message: string }>> {
    return $api.delete(`/files/${id}`);
  }

  // Отримати файли за сутністю
  async getFilesByEntity(
    entityType: string,
    entityId: number,
    category?: string,
  ): Promise<AxiosResponse<FileResponseDto[]>> {
    const params = category ? { category } : {};
    return $api.get(`/files/entity/${entityType}/${entityId}`, { params });
  }

  // Оновити ім'я файла
  async updateFileName(
    id: number,
    fileName: string,
  ): Promise<AxiosResponse<FileResponseDto>> {
    return $api.patch(`/files/${id}/name`, { fileName });
  }
}

export default new FilesApi();
